import { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Search,
  Calendar,
  User,
  Plus,
  Bell,
  FlaskConical,
  LogOut,
  Moon,
  Sun,
  Menu,
  X,
  Award,
  Users,
  BarChart3,
} from 'lucide-react';
import { useAuthStore } from '@/lib/store';
import { api } from '@/lib/api';

interface NavItem {
  to: string;
  label: string;
  icon: React.ElementType;
}

function getNavItems(role: string): NavItem[] {
  if (role === 'researcher') {
    return [
      { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
      { to: '/my-studies', label: 'My Studies', icon: FlaskConical },
      { to: '/create-study', label: 'Create Study', icon: Plus },
      { to: '/profile', label: 'Profile', icon: User },
    ];
  }

  // student / default
  return [
    { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { to: '/studies', label: 'Browse Studies', icon: Search },
    { to: '/appointments', label: 'My Appointments', icon: Calendar },
    { to: '/profile', label: 'Profile', icon: User },
  ];
}

function getAmbassadorItem(): NavItem {
  return { to: '/ambassador', label: 'Ambassador Portal', icon: Award };
}

export default function AppLayout() {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return document.documentElement.classList.contains('dark');
    }
    return false;
  });
  const [unreadCount, setUnreadCount] = useState(0);

  // Fetch unread notification count
  useEffect(() => {
    let mounted = true;
    const fetchCount = async () => {
      try {
        const data = await api.getUnreadCount();
        if (mounted) setUnreadCount(data.count);
      } catch {
        // silently fail
      }
    };
    fetchCount();
    const interval = setInterval(fetchCount, 30_000);
    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, []);

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Dark mode toggle
  const toggleDarkMode = () => {
    const next = !darkMode;
    setDarkMode(next);
    document.documentElement.classList.toggle('dark', next);
    localStorage.setItem('theme', next ? 'dark' : 'light');
  };

  // Initialize dark mode from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('theme');
    if (saved === 'dark' || (!saved && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
      document.documentElement.classList.add('dark');
      setDarkMode(true);
    }
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (!user) return null;

  const navItems = getNavItems(user.role);
  const isAmbassador = user.role === 'ambassador';

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50 dark:bg-gray-950">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden transition-opacity duration-300"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-50 w-72 flex flex-col
          transform transition-transform duration-300 ease-out
          lg:relative lg:translate-x-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {/* Sidebar background — gradient with glass */}
        <div className="absolute inset-0 bg-gradient-to-b from-gray-900 via-gray-900 to-gray-950 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950" />
        <div className="absolute inset-0 bg-gradient-to-br from-brand-600/10 via-transparent to-accent-600/10" />
        <div className="absolute inset-y-0 right-0 w-px bg-gradient-to-b from-brand-500/20 via-gray-700/50 to-accent-500/20" />

        {/* Content (relative so it sits above backgrounds) */}
        <div className="relative flex flex-col h-full">
          {/* Brand */}
          <div className="flex items-center justify-between px-6 py-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-500 to-accent-500 flex items-center justify-center shadow-lg shadow-brand-500/25">
                <FlaskConical className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-white tracking-tight">APEX</h1>
                <p className="text-[11px] text-gray-400 font-medium tracking-wide uppercase">Research Marketplace</p>
              </div>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Divider */}
          <div className="mx-4 h-px bg-gradient-to-r from-transparent via-gray-700/50 to-transparent" />

          {/* Nav links */}
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                    isActive
                      ? 'bg-gradient-to-r from-brand-600/20 to-brand-600/10 text-white shadow-sm'
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <div
                      className={`flex items-center justify-center w-9 h-9 rounded-lg transition-all duration-200 ${
                        isActive
                          ? 'bg-gradient-to-br from-brand-500 to-brand-600 text-white shadow-md shadow-brand-500/30'
                          : 'bg-gray-800/50 text-gray-400 group-hover:text-gray-200 group-hover:bg-gray-800'
                      }`}
                    >
                      <item.icon className="w-[18px] h-[18px]" />
                    </div>
                    <span>{item.label}</span>
                    {isActive && (
                      <div className="ml-auto w-1.5 h-1.5 rounded-full bg-brand-400 shadow-sm shadow-brand-400/50" />
                    )}
                  </>
                )}
              </NavLink>
            ))}

            {/* Ambassador link */}
            {isAmbassador && (
              <>
                <div className="mx-1 my-3 h-px bg-gradient-to-r from-transparent via-gray-700/50 to-transparent" />
                <NavLink
                  to={getAmbassadorItem().to}
                  className={({ isActive }) =>
                    `group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                      isActive
                        ? 'bg-gradient-to-r from-accent-600/20 to-accent-600/10 text-white shadow-sm'
                        : 'text-gray-400 hover:text-white hover:bg-white/5'
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <div
                        className={`flex items-center justify-center w-9 h-9 rounded-lg transition-all duration-200 ${
                          isActive
                            ? 'bg-gradient-to-br from-accent-500 to-accent-600 text-white shadow-md shadow-accent-500/30'
                            : 'bg-gray-800/50 text-gray-400 group-hover:text-gray-200 group-hover:bg-gray-800'
                        }`}
                      >
                        <Award className="w-[18px] h-[18px]" />
                      </div>
                      <span>Ambassador Portal</span>
                      {isActive && (
                        <div className="ml-auto w-1.5 h-1.5 rounded-full bg-accent-400 shadow-sm shadow-accent-400/50" />
                      )}
                    </>
                  )}
                </NavLink>
              </>
            )}

            {/* Notifications link */}
            <div className="mx-1 my-3 h-px bg-gradient-to-r from-transparent via-gray-700/50 to-transparent" />
            <NavLink
              to="/notifications"
              className={({ isActive }) =>
                `group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                  isActive
                    ? 'bg-gradient-to-r from-brand-600/20 to-brand-600/10 text-white shadow-sm'
                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <div
                    className={`relative flex items-center justify-center w-9 h-9 rounded-lg transition-all duration-200 ${
                      isActive
                        ? 'bg-gradient-to-br from-brand-500 to-brand-600 text-white shadow-md shadow-brand-500/30'
                        : 'bg-gray-800/50 text-gray-400 group-hover:text-gray-200 group-hover:bg-gray-800'
                    }`}
                  >
                    <Bell className="w-[18px] h-[18px]" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-danger-500 text-[10px] font-bold text-white shadow-lg shadow-danger-500/40 animate-pulse-soft">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </div>
                  <span>Notifications</span>
                  {unreadCount > 0 && !isActive && (
                    <span className="ml-auto inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full bg-danger-500/20 text-danger-400 text-[11px] font-semibold">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </>
              )}
            </NavLink>
          </nav>

          {/* Bottom section */}
          <div className="relative px-3 pb-4 space-y-3">
            {/* Dark mode toggle */}
            <div className="mx-1 h-px bg-gradient-to-r from-transparent via-gray-700/50 to-transparent" />
            <button
              onClick={toggleDarkMode}
              className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-sm font-medium text-gray-400 hover:text-white hover:bg-white/5 transition-all duration-200"
            >
              <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-gray-800/50 text-gray-400 hover:text-gray-200">
                {darkMode ? <Sun className="w-[18px] h-[18px]" /> : <Moon className="w-[18px] h-[18px]" />}
              </div>
              <span>{darkMode ? 'Light Mode' : 'Dark Mode'}</span>
            </button>

            {/* User card */}
            <div className="mx-1 h-px bg-gradient-to-r from-transparent via-gray-700/50 to-transparent" />
            <div className="p-3 rounded-xl bg-white/5 border border-white/5">
              <div className="flex items-center gap-3">
                {/* Avatar */}
                <div className="relative flex-shrink-0">
                  {user.avatar_url ? (
                    <img
                      src={user.avatar_url}
                      alt={user.first_name}
                      className="w-10 h-10 rounded-xl object-cover ring-2 ring-white/10"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-500 to-accent-500 flex items-center justify-center text-white font-semibold text-sm shadow-lg shadow-brand-500/20">
                      {user.first_name?.[0]}
                      {user.last_name?.[0]}
                    </div>
                  )}
                  <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-success-500 border-2 border-gray-900" />
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white truncate">
                    {user.first_name} {user.last_name}
                  </p>
                  <p className="text-[11px] text-gray-500 truncate">{user.email}</p>
                  {user.role === 'student' && (
                    <div className="flex items-center gap-1.5 mt-1">
                      <div className="flex items-center gap-1">
                        <BarChart3 className="w-3 h-3 text-brand-400" />
                        <span className="text-[11px] font-medium text-brand-400">
                          {Math.round(user.reliability_score)}% reliable
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Logout */}
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 w-full mt-3 px-3 py-2 rounded-lg text-xs font-medium text-gray-400 hover:text-danger-400 hover:bg-danger-500/10 transition-all duration-200"
              >
                <LogOut className="w-3.5 h-3.5" />
                <span>Sign out</span>
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 flex flex-col min-h-0 overflow-hidden">
        {/* Top bar (mobile) */}
        <header className="lg:hidden flex items-center justify-between px-4 py-3 bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl border-b border-gray-200/50 dark:border-gray-800/50">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-xl text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <Menu className="w-5 h-5" />
          </button>

          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-brand-500 to-accent-500 flex items-center justify-center">
              <FlaskConical className="w-4 h-4 text-white" />
            </div>
            <span className="text-base font-bold text-gray-900 dark:text-white tracking-tight">APEX</span>
          </div>

          <NavLink to="/notifications" className="relative p-2 rounded-xl text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
            <Bell className="w-5 h-5" />
            {unreadCount > 0 && (
              <span className="absolute top-1 right-1 min-w-[16px] h-[16px] px-1 flex items-center justify-center rounded-full bg-danger-500 text-[9px] font-bold text-white">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </NavLink>
        </header>

        {/* Page content */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
            <Outlet />
          </div>
        </div>
      </main>
    </div>
  );
}
